//================================================================
//  Page: RequestDetails 
//================================================================

//  Purpose: Detailed Request page

//================================================================

//Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import SideNav from '../../../Components/SideNav/SideNav';
import BreadCrumbs from '../../../Components/Breadcrumbs/BreadCrumbs';
import TabView from '../../../Components/TabView/TabView';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';
import ActivityFeed from '../../../Components/ActivityFeed/ActivityFeed';

// > Digital Request forms 
import NewInitiative from '../../../Components/DigitalRequestForms/NewInitiative';
import ContractRenewal from '../../../Components/DigitalRequestForms/ContractRenewal';
import Enhancement from '../../../Components/DigitalRequestForms/Enhancement';
import AdditionalLicense from '../../../Components/DigitalRequestForms/AdditionalLicense';
import Decommission from '../../../Components/DigitalRequestForms/Decommission';

// > Commercial Request Forms
import EstablishNewAgreement from '../../../Components/CommercialRequestForms/EstablishNewAgreement';
import VaryExtendRenewExistingAgreement from '../../../Components/CommercialRequestForms/VaryExtendRenewExistingAgreement';
import TerminateExistingAgreement from '../../../Components/CommercialRequestForms/TerminateExistingAgreement';
import AssignNovateAgreement from '../../../Components/CommercialRequestForms/AssignNovateAgreement';
import EstablishNDA from '../../../Components/CommercialRequestForms/EstablishNDA';

//Functions
import DocumentListener from '../../../Library/DocumentListener';
import ConvertDate from '../../../Library/ConvertDate';
import WriteDocument from '../../../Library/WriteDocument';
import AddRemoveDocumentArray from '../../../Library/AddRemoveDocumentArray';

//Images
import Expand from '../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../Components/Images/Icon_Expand_Teal.svg';

//CSS
import '../Account.css';


export default function RequestDetails() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const params = useParams();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine status of the page > 'pending', 'onload', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Contains Firestore Document of the request
    const [requestDoc, setRequestDoc] = useState(undefined);

    // For changing request status dropdown > 'Escalate', 'Cancel'
    const [requestStatus, setRequestStatus] = useState('');

    // Some status changes require an additional comment
    const [changeStatusComment, setChangeStatusComment] = useState('');

    // Used to toggle visibility of the 'View More Details' section
    const [viewDetails, setViewDetails] = useState(true);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler
    function handleSubmit() {

        if (requestStatus === 'Cancel') {

            const docPromises = [];

            // Updated Status
            const docObject = {
                'status': 'Cancelled',
                'cancellationreason': changeStatusComment,
                'lastmodifiedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'lastmodifieddate': new Date(),
            };

            docPromises.push(
                WriteDocument('requests', requestDoc?.requestid, docObject, true)
            );

            // Updated Activity Feed
            const updatedActivityFeed =  {
                'action': 'cancelled',
                'actionedby': {
                    'email': getUser.emailaddress,
                    'givenname': getUser.givenname,
                    'surname': getUser.surname,
                },
                'activitydate': new Date(),
                'comments': changeStatusComment
            };

            docPromises.push(
                AddRemoveDocumentArray('requests', requestDoc?.requestid, 'activityfeed', updatedActivityFeed, 'add', updatedActivityFeed.actionedby)
            );

            return Promise.all(docPromises)
            .then(() => {

                setRequestStatus('');
                setChangeStatusComment('');

            })
            .catch((err) => {

                console.log(err);
                setPageStatus('error-fatal');

            })
        }

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Set up document listener for digital request document from Firestore
    useEffect(() => {

        const requestid = params.id;

        function onLoadChange(document) {

            if (!(document?.tasktype === 'Digital Request' || document?.tasktype === 'Digital Commercial Request')) return setPageStatus('forbidden');

            setRequestDoc(document);
            setPageStatus('onload');

        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = DocumentListener('requests', requestid, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        }

        // eslint-disable-next-line
    }, [pageStatus]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <div className='Account-Container height-[100vh] overflow-hidden'>

            {/* Main Page */}
            <div className='Account-Body'>
                <PageComponent
                    status={pageStatus}
                    header={
                        <div className='flex flex-col p-4 h-28 bg-neutral-100 w-full border-b border-b-neutral-300 border-1' hidden={pageStatus !== 'onload'}>
                            <BreadCrumbs rootLinkName={'My Requests'} rootLinkRoute={'/requests'} childLinkName={requestDoc?.parentrequestid?.toUpperCase()}></BreadCrumbs>
                            <h4 className='flex items-center cursor-default text-[22px]'>{requestDoc?.taskname}</h4>
                        </div>
                    }
                    body={
                        <div className={`Request-Layout ${requestDoc?.status !== 'Not Started' && requestDoc?.status !== 'In Progress' ? 'Request-Actioned' : ''}`}>

                            {/* REQUEST INFORMATION */}
                            <div className='Request-Summary h-[calc(100vh-225px)] overflow-y-auto'>
                                <div className='flex flex-col gap-6 p-8'>

                                    {/* Name */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Name</label>
                                        <p className='m-0'>{requestDoc?.taskname}</p>
                                    </div>

                                    {/* Short Description */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Short Description</label>
                                        <p className='m-0'>{requestDoc?.taskdescription}</p>
                                    </div>

                                    {/* Assigned To */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Assigned To</label>
                                        <div className='flex flex-col gap-1'>
                                            <p className='m-0' hidden={requestDoc?.assignedto?.email === 'Unassigned'}>
                                                {requestDoc?.assignedto?.givenname} {requestDoc?.assignedto?.surname}
                                            </p>
                                            <p className={`m-0 text-[#424242] ${requestDoc?.assignedto?.email !== 'Unassigned' && 'text-sm'}`}>
                                                {requestDoc?.assignedto?.email}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Status</label>
                                        <StatusLabel status={requestDoc?.status} tasktype={requestDoc?.tasktype}></StatusLabel>
                                    </div>

                                    {/* Created Date */}
                                    <div className='flex flex-col gap-3'>
                                        <label className='font-semibold'>Created Date</label>
                                        <p className='m-0'>{ConvertDate(requestDoc?.createddate)}</p>
                                    </div>

                                </div>

                                <hr className='text-neutral-500 p-0 m-0'></hr>

                                {/* VIEW MORE DETAILS */}
                                <div className='flex flex-row gap-3 px-3 py-2 items-center justify-between cursor-pointer' onClick={() => setViewDetails(!viewDetails)}>

                                    {/* Title */}
                                    <div className='flex flex-row items-center gap-2 py-2'>
                                        <img className='cursor-pointer' src={viewDetails ? Expand : Collapse} alt={'>'}></img>
                                        <h4 className='text-[16px] m-0'> {viewDetails ? 'View Less Details' : 'View More Details'} </h4>
                                    </div>

                                </div>

                                <div className='flex flex-row w-full justify-between pb-4' hidden={!viewDetails}>
                                    <TabView
                                        defaultView={1}
                                        oneTitle={'Request Details'}
                                        oneContent={(() => {
                                            switch (requestDoc?.requesttype) {
                                                
                                                // Digital Request Forms
                                                case 'New Initiative or Technology': return <NewInitiative task={requestDoc}></NewInitiative>;
                                                case 'Contract Renewal': return <ContractRenewal task={requestDoc}></ContractRenewal>;
                                                case 'Enhancement': return <Enhancement task={requestDoc}></Enhancement>;
                                                case 'Additional Licenses or Overages': return <AdditionalLicense task={requestDoc}></AdditionalLicense>;
                                                case 'Decommission': return <Decommission task={requestDoc}></Decommission>;

                                                // Commercial Request Forms
                                                case 'Establish New Agreement': return <EstablishNewAgreement task={requestDoc}></EstablishNewAgreement>;
                                                case 'Vary/Extend/Renew Existing Agreement': return <VaryExtendRenewExistingAgreement task={requestDoc}></VaryExtendRenewExistingAgreement>;
                                                case 'Terminate Existing Agreement': return <TerminateExistingAgreement task={requestDoc}></TerminateExistingAgreement>;
                                                case 'Assign/Novate Agreement': return <AssignNovateAgreement task={requestDoc}></AssignNovateAgreement>;
                                                case 'Establish NDA': return <EstablishNDA task={requestDoc}></EstablishNDA>;


                                                default: return 'Form not found';
                                            }
                                        })()}
                                    ></TabView>
                                </div>

                                <hr className='text-neutral-500 p-0 m-0'></hr>

                                {/* TRACKING HISTORY */}
                                <div className='p-2'>
                                    <TabView
                                        defaultView={1}
                                        oneTitle={'Tracking History'}
                                        oneContent={
                                            <div className='px-2'>
                                                <ActivityFeed
                                                    task={requestDoc}
                                                ></ActivityFeed>
                                            </div>
                                        }
                                    ></TabView>
                                </div>
                            </div>

                            {/* MANAGE REQUEST */}
                            <div className='Manage-Request flex flex-col justify-between overflow-y-auto h-[calc(100vh-234px)]'>

                                {/* Change Status */}
                                <div>
                                    <label className='p-4 font-semibold text-lg border-b-neutral-300 border-b w-full'>Manage Request</label>

                                    <div className='flex flex-col p-4 gap-4'>

                                        <label className='font-semibold'> Change status:</label>

                                        <select className='Input-Field-Select' value={requestStatus} disabled={(!['Not Started', 'In Progress'].includes(requestDoc?.status))} onChange={(e) => setRequestStatus(e.target.value)}>
                                            <option hidden value=''>{requestDoc?.status !== 'In Progress' && requestDoc?.status !== 'Not Started' ? requestDoc?.status : 'Select'}</option>
                                            {/* <option value='Escalate'>Escalate</option>  */}
                                            <option className='text-[#DE0000]' value='Cancel'>Cancel</option>
                                        </select>

                                        {/* Only shows when an option is selected */}
                                        <div hidden={requestStatus.length === 0} className='flex flex-col gap-4'>
                                            <label className='font-semibold'> Comment: <span className='text-[#C4314B]'>*</span> </label>
                                            <textarea
                                                className='Input-Field-TextArea'
                                                placeholder={`Add a reason for ${requestStatus === 'Escalate' ? 'escalating' : 'cancelling'} this request.`}
                                                value={changeStatusComment}
                                                onChange={(e) => setChangeStatusComment(e.target.value)}
                                            ></textarea>
                                        </div>

                                    </div>
                                </div>

                                {/* Footer */}
                                <div className='border-t-neutral-300 border-t w-full place-self-end py-3 px-4 flex flex-row gap-4'>

                                    {/* Proceed */}
                                    <button className='Primary-Button' disabled={changeStatusComment.length === 0} onClick={() => handleSubmit()}>
                                        Proceed
                                    </button>

                                    {/* Cancel */}
                                    <button className='Secondary-Button' disabled={requestStatus === ''} onClick={() => {
                                        setRequestStatus('');
                                        setChangeStatusComment('');
                                    }}
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </div>}
                ></PageComponent>
            </div>

            {/* Side Menu */}
            <div className='Account-SideNav h-[calc(100vh-133px)]'>
                <SideNav></SideNav>
            </div>

        </div>

    )
}